import { v1 as uuid } from 'uuid';
import {
  map,
  omitBy,
  pick,
  isUndefined,
  some,
  every,
  find,
  get,
  trim,
  sortBy,
  range,
} from 'lodash';

import { clearQuotaCompletesFromValues } from 'containers/TargetingPeeqPage/Criteria/helpers';

import {
  NEW_AGE_RANGE_MIN_13,
  NEW_AGE_RANGE_MIN_16,
  NEW_AGE_RANGE_MIN_18,
  AGE_LIMITS_MIN_13,
  AGE_LIMITS_MIN_16,
  AGE_LIMITS_MIN_18,
  AGE_RANGE_MIN_DIFFERENCE_CONSTRAINT,
  COUNTRY_IDS_WITH_MIN_AGE_13,
} from './constants';

const isOverlap = (rangeA, rangeB) =>
  rangeA.min <= rangeB.max && rangeA.max >= rangeB.min;

const isIdenticalRanges = (rangeA, rangeB) =>
  rangeA.min === rangeB.min && rangeA.max === rangeB.max;

export const defaultAges = () => [
  { value: '18 - 24', label: '18 - 24', min: 18, max: 24, refId: uuid() },
  { value: '25 - 34', label: '25 - 34', min: 25, max: 34, refId: uuid() },
  { value: '35 - 44', label: '35 - 44', min: 35, max: 44, refId: uuid() },
  { value: '45 - 54', label: '45 - 54', min: 45, max: 54, refId: uuid() },
  { value: '55 - 64', label: '55 - 64', min: 55, max: 64, refId: uuid() },
];

export const canDisableValues = (values) => values.length > 1;

export const canHaveQuota = (values) =>
  values.length > 1 && values.length < 101;

export const getTransformedValues = (
  { isQuotasEnabled, values },
  properties,
) => {
  const cleanValues = values.map((age) =>
    omitBy(pick(age, properties), isUndefined),
  );

  return isQuotasEnabled
    ? cleanValues
    : clearQuotaCompletesFromValues(cleanValues);
};

export const valueToMinMax = (value, minAge, maxAge) => {
  let min;
  let max;

  if (value.includes('>')) {
    // eslint-disable-next-line no-unused-vars
    const minLimit = value.split('>').map(trim).map(Number)[1];
    [min, max] = [minLimit + 1, maxAge];
  } else {
    [min, max] = value.split(/\s*-\s*/).map(Number);
  }
  return {
    min: Math.max(min || minAge, minAge),
    max: Math.min(max || maxAge, maxAge),
    original: {
      min,
      max,
    },
  };
};
export const minMaxToValue = (min, max) => `${min} - ${max}`;

export const getValidationErrors = (formValues) => {
  const { values } = formValues;
  const errors = {};

  if (!values.length) {
    errors.noValueSelected = true;
  }

  const anyRangeWithWrongDifference = some(
    values,
    (value) => value.max - value.min < AGE_RANGE_MIN_DIFFERENCE_CONSTRAINT,
  );
  if (anyRangeWithWrongDifference) {
    errors.rangeDifference = true;
  }

  const anyRangeWithWrongOrder = some(values, (value) => value.max < value.min);
  if (anyRangeWithWrongOrder) {
    errors.rangeOrder = true;
  }

  const anyRangeOverlaps = some(values, (rangeA, indexA) =>
    some(
      values,
      (rangeB, indexB) => indexA !== indexB && isOverlap(rangeA, rangeB),
    ),
  );

  if (anyRangeOverlaps) {
    errors.rangeOverlap = true;
  }

  const hasIdenticalRanges = some(values, (rangeA, indexA) =>
    some(
      values,
      (rangeB, indexB) =>
        indexA !== indexB && isIdenticalRanges(rangeA, rangeB),
    ),
  );

  if (hasIdenticalRanges) {
    errors.identicalRanges = true;
  }

  return errors;
};

export const getInitializedQuota = (values, enabled, completes) => {
  if (!enabled) {
    return values;
  }

  const quotaIsNotInitialized = every(
    values,
    (age) => get(age, 'quotaCompletes', false) === false,
  );

  if (quotaIsNotInitialized) {
    const count = values.length;

    const itemQuotaCompletes = Math.floor(completes / count);
    const lastItemQuotaCompletes = completes - itemQuotaCompletes * (count - 1);
    const lastItemId = values[count - 1].refId;

    return values.map((age) => {
      const isLastItem = age.refId === lastItemId;
      const isActiveItem = find(values, ({ refId }) => age.refId === refId);

      const quotaCompletes = isLastItem
        ? lastItemQuotaCompletes
        : itemQuotaCompletes;

      if (isActiveItem) {
        return {
          ...age,
          quotaCompletes,
        };
      }

      return age;
    });
  }

  return values;
};

export const hasAdjustedAges = (values) => some(values, 'adjusted');

export const prepareValues = (values = [], ageLimits, ordered = true) => {
  const valuesToUse = map(values, (age) => {
    const { min, max, original } = valueToMinMax(
      age.value,
      ageLimits.MIN,
      ageLimits.MAX,
    );
    let adjusted = false;
    const refId = age.refId || uuid();
    let { label, value } = age;
    if (original.min !== min || original.max !== max) {
      adjusted = true;
      value = minMaxToValue(min, max);
    }
    label = minMaxToValue(min, max);

    return {
      ...age,
      value,
      label,
      min,
      max,
      refId,
      adjusted,
    };
  });

  return ordered ? sortBy(valuesToUse, 'min') : valuesToUse;
};

export function getNewAgeRange({ countryId, hideAgeUnder18 }) {
  if (hideAgeUnder18) {
    return NEW_AGE_RANGE_MIN_18;
  }

  if (COUNTRY_IDS_WITH_MIN_AGE_13.includes(countryId)) {
    return NEW_AGE_RANGE_MIN_13;
  }

  return NEW_AGE_RANGE_MIN_16;
}

export function getAgeLimits({ countryId, hideAgeUnder18 }) {
  if (hideAgeUnder18) {
    return AGE_LIMITS_MIN_18;
  }

  if (COUNTRY_IDS_WITH_MIN_AGE_13.includes(countryId)) {
    return AGE_LIMITS_MIN_13;
  }

  return AGE_LIMITS_MIN_16;
}

export function convertAgeGroupsToRanges(group) {
  const ageValues = group.values.sort(
    (ageA, ageB) => parseInt(ageA, 10) - parseInt(ageB, 10),
  );
  const value = `${ageValues[0]} - ${ageValues[ageValues.length - 1]}`;

  const ageRange = { value, label: value };

  if (!isUndefined(group.quotaCompletes)) {
    ageRange.quotaCompletes = group.quotaCompletes;
  }

  return ageRange;
}

export function checkIsDefaultAges(values) {
  const ageDefaults = defaultAges();
  if (values.length !== ageDefaults.length) {
    return false;
  }

  const defaultAgesValues = ageDefaults.map(({ value }) => value);
  const valuesToCheck = values.map(({ value }) => value);

  return every(defaultAgesValues, (defaultAge) =>
    valuesToCheck.includes(defaultAge),
  );
}

export function selectedAgeValuesAreTheSameWithAgeLimits(ageLimits, values) {
  if (!values || !values.length || !ageLimits) {
    return false;
  }
  const ageRange = range(ageLimits.MIN, ageLimits.MAX + 1);
  const selectedAges = values.reduce(
    (acc, { min, max }) => acc.concat(range(min, max + 1)),
    [],
  );
  const uniqueSelectedAges = [...new Set(selectedAges)];
  return (
    ageRange.length === uniqueSelectedAges.length &&
    every(ageRange, (age) => uniqueSelectedAges.includes(age))
  );
}
